import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase/config';
import { setUser, fetchEmployees, checkRecruitmentAssessorAccess } from './store/slices/authSlice';
import { query, collection, where, getDocs } from 'firebase/firestore';
import LogoutConfirmationModal from './components/LogoutConfirmationModal';
import './App.scss';

// Pages
import Login from './pages/Login';
import Profile from './pages/Profile';
import MyTasks from './pages/MyTasks';
import PerformanceReview from './pages/PerformanceReview';
import GameTesting from './pages/GameTesting';
import TestingAnalytics from './pages/TestingAnalytics';
import BugResolution from './pages/BugResolution';
import BugsAssignedToMe from './pages/BugsAssignedToMe';
import ClaimManagement from './pages/ClaimManagement';
import MySubscriptions from './pages/MySubscriptions';
import LeaveManagement from './pages/LeaveManagement';
import WorkRemotely from './pages/WorkRemotely';
import Help from './pages/Help';
import PPCTesting from './pages/PPCTesting';
import AdminLayout from './layouts/AdminLayout';
import Notifications from './pages/Notifications';
import KnowMyelinners from './pages/KnowMyelinners';
import KnowAdvisors from './pages/KnowAdvisors';
import HRPolicies from './pages/HRPolicies';
import CultureTenets from './pages/CultureTenets';
import Registration from './pages/Registration';

// Admin Pages
import Blogs from './pages/admin/Blogs';
import EditBlog from './pages/admin/Blogs/components/EditBlog';
import News from './pages/admin/News';
import EditNews from './pages/admin/News/components/EditNews';
import Documents from './pages/admin/Documents';
import Gallery from './pages/admin/Gallery';
import JobPosts from './pages/admin/JobPosts';
import Applications from './pages/admin/JobPosts/Applications';
import AllApplications from './pages/admin/JobPosts/AllApplications';
import AddEmployee from './pages/admin/AddEmployee';
import EditEmployee from './pages/admin/EditEmployee';
import ViewEmployees from './pages/admin/ViewEmployees';
import ArchivedUsers from './pages/admin/ArchivedUsers';
import Advisors from './pages/admin/Advisors';
import Archive from './pages/admin/Archive';
import AssignTasks from './pages/admin/AssignTasks/index.js';
import AdminGameTesting from './pages/admin/AdminGameTesting';
import Approvals from './pages/admin/Approvals';
import Registrations from './pages/admin/Registrations';
import AdminPerformanceReview from './pages/admin/PerformanceReview';
import Payslips from './pages/admin/Payslips';
import SubscriptionsTracker from './pages/admin/SubscriptionsTracker';
import DomainMigration from './pages/admin/DomainMigration';
import TerminateEmployees from './pages/admin/TerminateEmployees';
import AdminRecruitment from './pages/admin/Recruitment';
import AssessorRecruitment from './pages/Recruitment';

// Components
import EmployeesList from './components/EmployeesList';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, user } = useSelector(state => state.auth);
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin && user?.requiresProfileCompletion && location.pathname !== '/') {
    return <Navigate to="/" />;
  }
  
  return children;
};

// Admin-only route component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, adminViewActive, loading } = useSelector(state => state.auth);
  
  console.log('AdminRoute checking access:', { isAuthenticated, isAdmin, adminViewActive, loading });
  
  if (loading) {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    console.log('AdminRoute: Not authenticated, redirecting to login');
    return <Navigate to="/login" />;
  }
  
  // Need both to be admin AND have admin view active
  if (!isAdmin || !adminViewActive) {
    console.log('AdminRoute: Not in admin view, redirecting to home');
    return <Navigate to="/" />;
  }
  
  console.log('AdminRoute: Access granted for admin');
  return children;
};

const AssessorRoute = ({ children }) => {
  const { isAuthenticated, canAssessRecruitment, isAdmin, loading } = useSelector(
    (state) => state.auth
  );

  if (loading) {
    return (
      <div className="route-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Admins should always be able to open assessor view.
  if (!canAssessRecruitment && !isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

const AuthHandler = ({ children }) => {
  const dispatch = useDispatch();
  const [initializing, setInitializing] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  
  useEffect(() => {
    console.log('AuthHandler initializing...');
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User authenticated' : 'User not authenticated');
      setAuthError(null);
      
      if (user) {
        try {
          // Check if user exists in myeliners collection
          const myelinersRef = collection(db, 'myeliners');
          const q = query(myelinersRef, where('email', '==', user.email));
          
          try {
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              // Get the first document
              const userDoc = querySnapshot.docs[0];
              const userData = userDoc.data();
              
              // Determine user type
              const userType = userData.userType || 'user';
              console.log('User document found:', { email: user.email, userType });
              
              // Set user with additional data
              dispatch(setUser({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || userData.name || user.email.split('@')[0],
                photoURL: user.photoURL,
                userType,
                empID: userData.empID,
                requiresProfileCompletion:
                  !String(userData.empID || '').trim() ||
                  !String(userData.designation || '').trim() ||
                  !String(userData.phone || '').trim() ||
                  !String(userData.address || '').trim() ||
                  !String(userData.country || '').trim(),
              }));
              
              // Fetch all employees
              dispatch(fetchEmployees());
              dispatch(checkRecruitmentAssessorAccess(user.email));
            } else {
              // User not found in myeliners collection
              console.log('User not found in myeliners collection:', user.email);
              setAuthError('User not found in employee database');
              dispatch(setUser(null));
            }
          } catch (firestoreError) {
            console.error("Firestore error:", firestoreError);
            setAuthError(`Firestore error: ${firestoreError.message}`);
            // Still set user with default user type to prevent complete lockout
            dispatch(setUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              photoURL: user.photoURL,
              userType: 'user', // Default to regular user on error
            }));
            dispatch(checkRecruitmentAssessorAccess(user.email));
          }
        } catch (error) {
          console.error("General error checking user:", error);
          setAuthError(`Error: ${error.message}`);
          dispatch(setUser(null));
        }
      } else {
        dispatch(setUser(null));
      }
      
      // Set initializing to false once we've checked auth state
      setInitializing(false);
    });
    
    return () => unsubscribe();
  }, [dispatch]);
  
  // Show loading indicator while checking auth state
  if (initializing) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }
  
  if (authError) {
    return (
      <div className="app-error">
        <h2>Authentication Error</h2>
        <p>{authError}</p>
        <button onClick={() => setIsLogoutModalOpen(true)}>Logout</button>
        
        {/* Logout Confirmation Modal */}
        <LogoutConfirmationModal 
          isOpen={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
        />
      </div>
    );
  }
  
  return children;
};

// Add a AuthDebug component
const AuthDebug = () => {
  const authState = useSelector(state => state.auth);
  
  return (
    <div style={{ padding: '20px' }}>
      <h1>Auth Debug</h1>
      <pre>{JSON.stringify(authState, null, 2)}</pre>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AuthHandler>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/debug" element={<AuthDebug />} />
          
          {/* User Routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-md-4">
                    <Profile />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/my-tasks" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-md-4">
                    <MyTasks />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/performance-review" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <PerformanceReview />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/game-testing" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <GameTesting />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/testing-analytics" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <TestingAnalytics />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bug-resolution" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <BugResolution />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/bugs-assigned-to-me" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <BugsAssignedToMe />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/claim-management" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <ClaimManagement />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/my-subscriptions" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <MySubscriptions />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/leave-management" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <LeaveManagement />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/work-remotely" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <WorkRemotely />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          

          
          <Route 
            path="/recruitment/reviews" 
            element={
              <AssessorRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AssessorRecruitment />
                  </div>
                </AdminLayout>
              </AssessorRoute>
            } 
          />

          <Route 
            path="/help" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Help />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/ppc-feedback" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <PPCTesting />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Notifications />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/know-myelinners" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <KnowMyelinners />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/know-advisors" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <KnowAdvisors />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/hr-policies" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <HRPolicies />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/culture-tenets" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <CultureTenets />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/users" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <EmployeesList />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/myeliner/:email" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Profile />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/settings" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">Settings Page</div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/blogs" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Blogs />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/blogs/edit/:id" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <EditBlog />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/news" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <News />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/news/edit/:id" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <EditNews />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/documents" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Documents />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/gallery" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Gallery />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/jobs" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <JobPosts />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/job-posts" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <JobPosts />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/applications" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AllApplications />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />

          <Route 
            path="/admin/job-posts/:jobId/applications" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Applications />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/assign-tasks" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AssignTasks />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/game-testing" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AdminGameTesting />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/add-employee" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AddEmployee />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/edit-employee" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <EditEmployee />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/view-employees" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <ViewEmployees />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/archived-users" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <ArchivedUsers />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/advisors" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Advisors />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/archive" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Archive />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/approvals" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Approvals />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/registrations" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Registrations />
                  </div>
                </AdminLayout>
              </AdminRoute>
            }
          />
          
          <Route 
            path="/admin/terminate" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <TerminateEmployees />
                  </div>
                </AdminLayout>
              </AdminRoute>
            }
          />
          
          <Route 
            path="/admin/performance-review" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AdminPerformanceReview />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/payslips" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <Payslips />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/subscriptions-tracker" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <SubscriptionsTracker />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />
          
          <Route 
            path="/admin/recruitment" 
            element={
              <AdminRoute>
                <AdminLayout>
                  <div className="p-4">
                    <AdminRecruitment />
                  </div>
                </AdminLayout>
              </AdminRoute>
            } 
          />

          <Route 
            path="/admin/domain-migration" 
            element={
              <ProtectedRoute>
                <AdminLayout>
                  <div className="p-4">
                    <DomainMigration />
                  </div>
                </AdminLayout>
              </ProtectedRoute>
            } 
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthHandler>
    </Router>
  );
};

export default App;